// @givmo/passkeys — the passkey sign-in DARK GATE (AS-WEB2's, lifted from `apps/admin` by AS-WEB12 so
// every door that offers the passkey sign-in shares one: the employer portal's `/login-home` and the
// charity console's director door).
//
// Whether to render the passkey sign-in at all. There is deliberately NO client-side flag here: the
// backend's assertion routes 404 while its passkey flag is off or its RP id is unset (staging and
// production today; dev runs them on since AS-BE40), so asking the server IS the gate, and there is
// nothing to keep in step when it flips. A second, client-side switch would only add a way for the two
// to disagree — the same reasoning that left AS-BE9's step-up gate without one (`agent_docs/auth.md`).
//
// The probe never surfaces anything: no toast, no console line, no telemetry — while dark, a 404 is
// the EXPECTED answer, and logging it would put a line in every visitor's console on every visit to
// the sign-in screen, in production.
//
// ⚠ The challenge the probe receives is DISCARDED. It is single-use and short-lived, so a click
// minutes later must start from a fresh one; `signInWithPasskey` always fetches its own.
//
// ══ WHY THE VERDICT IS CACHED IN sessionStorage, NOT JUST IN MEMORY ═══════════════════════════════
//
// The options route is guarded per IP, fail-closed, by `AUTHN_PASSKEY_ASSERTION_IP_RATE_LIMIT` /
// `_WINDOW_SECONDS` (AS-BE42: a per-environment operator knob, Terraform-wired — **20 per IP per
// hour** by default, 50 on dev) on the SAME `authn_passkey_assertion_options_ip` class a real passkey
// sign-in consumes. So a probe is not free traffic: once the feature is live it spends budget
// genuine sign-ins need, and a shared office NAT can exhaust a window's allowance between them.
// Sizing the knob per environment is the backend's lever; caching the verdict is this surface's.
//
// Two layers, therefore:
//   • the module memo bounds it to one probe per PAGE LOAD (and absorbs React's dev double-mount);
//   • sessionStorage bounds it to one probe per TAB per TTL — it survives reloads and same-tab
//     navigation, and a duplicated/auxiliary tab inherits a COPY, but an independently opened tab
//     gets a fresh store and pays for its own probe. `sessionStorage` is per-origin per-TAB, not
//     per-browser; `localStorage` would span tabs but would also outlive the browsing session, which
//     is the wrong lifetime for a verdict that a deploy can invalidate.
//
// The TTLs are asymmetric on purpose:
//   • `available` caches LONGEST — it cannot become false without a deploy, and it is the state whose
//     re-probe competes with real sign-ins for the scarce budget.
//   • `unavailable` caches shorter, so a browser left open across the flip round picks the feature up
//     reasonably soon. It is also the state that costs nothing TODAY: while dark the backend raises
//     `PasskeysDisabledException` from a ROUTER-LEVEL dependency, which FastAPI resolves before the
//     in-body rate-limit guard ever runs — so a dark 404 never touches the limiter. The starvation
//     risk is entirely a post-flip one.
//   • `rateLimited` caches BRIEFLY and never as `unavailable`: a 429 says nothing about whether the
//     feature exists, and treating it as absence would hide the button for the full negative TTL over
//     a transient burst.
//   • `unsupported` is not cached at all — no request was spent reaching it, so there is nothing to
//     save, and recomputing it is a property read.
//
// One key, every door: `sessionStorage` is per ORIGIN, so each console keeps its own verdict for its
// own origin, and two doors in one console (none today) would share one probe and one verdict.

import { useEffect, useState } from 'react';
import type { PasskeyAvailability } from '@givmo/auth';

/** Versioned so a later change to the stored shape cannot be misread as a valid old entry. */
const STORAGE_KEY = 'givmo.passkey-availability.v1';

/** How long each verdict may be trusted, in ms. See the header for why these differ. */
const TTL_MS: Record<PasskeyAvailability, number> = {
  available: 60 * 60 * 1000,
  unavailable: 30 * 60 * 1000,
  rateLimited: 5 * 60 * 1000,
  // Never persisted (no request was spent); present only so the map stays exhaustive.
  unsupported: 0,
};

interface CachedVerdict {
  verdict: PasskeyAvailability;
  expiresAt: number;
}

/** The in-flight or settled probe for this page load. */
let probe: Promise<PasskeyAvailability> | null = null;

/**
 * Drop the memoised probe, and by default the stored verdict too. A TEST SEAM — production never
 * calls it, because a flag flip arrives with a fresh document anyway. Without it, the first suite to
 * resolve the probe would decide the answer for every suite after it in the same module registry.
 *
 * `keepStoredVerdict: true` drops ONLY the per-page-load memo, which is what a browser does on a
 * reload: same session, same `sessionStorage`, fresh module registry. That is the scenario the
 * caching exists for, so it needs to be expressible.
 */
export function resetPasskeySignInAvailabilityProbe(
  options: { keepStoredVerdict?: boolean } = {},
): void {
  probe = null;
  if (options.keepStoredVerdict === true) return;
  try {
    globalThis.sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // Storage is a convenience here, never a requirement — see `readCachedVerdict`.
  }
}

/**
 * The stored verdict when one is present and unexpired, else `null`.
 *
 * Every access is guarded: `sessionStorage` THROWS on access in some privacy modes and inside a
 * partitioned third-party context, and a storage fault must degrade to "probe again" — never to a
 * sign-in screen that fails to render.
 */
function readCachedVerdict(now: number): PasskeyAvailability | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    if (typeof raw !== 'string') return null;
    const parsed = JSON.parse(raw) as Partial<CachedVerdict>;
    if (typeof parsed.verdict !== 'string' || typeof parsed.expiresAt !== 'number') return null;
    if (!(parsed.verdict in TTL_MS)) return null;
    return parsed.expiresAt > now ? (parsed.verdict as PasskeyAvailability) : null;
  } catch {
    return null;
  }
}

/** Persist a verdict for its TTL. A zero TTL (`unsupported`) is not worth a write. */
function writeCachedVerdict(verdict: PasskeyAvailability, now: number): void {
  const ttl = TTL_MS[verdict];
  if (ttl <= 0) return;
  try {
    const entry: CachedVerdict = { verdict, expiresAt: now + ttl };
    globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Full quota or a blocked store — the in-memory memo still bounds this page load.
  }
}

/** Resolve the verdict, preferring a stored one. The caching, not the probing, is what this adds. */
async function resolveAvailability(
  probeAvailability: () => Promise<PasskeyAvailability>,
): Promise<PasskeyAvailability> {
  const cached = readCachedVerdict(Date.now());
  if (cached !== null) return cached;
  const verdict = await probeAvailability();
  writeCachedVerdict(verdict, Date.now());
  return verdict;
}

/**
 * Whether the passkey sign-in should render: the browser can run a WebAuthn ceremony AND the backend
 * serves the assertion routes. `probeAvailability` is the door's own probe (`probePasskeySignIn` over
 * its assertion transport, as an auth context exposes it); keep it stable across renders, as an auth
 * context's memoised value is. Starts `false` and only ever flips to `true`, so nothing renders while
 * the verdict is being resolved and a dark environment renders nothing at all.
 */
export function usePasskeySignInAvailability(
  probeAvailability: () => Promise<PasskeyAvailability>,
): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let subscribed = true;
    probe ??= resolveAvailability(probeAvailability);
    void probe.then((verdict) => {
      if (subscribed) setAvailable(verdict === 'available');
    });
    return () => {
      subscribed = false;
    };
  }, [probeAvailability]);

  return available;
}
